import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { Badge, Button, Form, Progress, Select, Spin, Tooltip, message } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { t } from 'i18next';
import fileDownload from 'js-file-download';
import { useEffect, useRef, useState } from 'react';
import ExtraTab from 'src/app/components/ExtraTabcomponent/ExtraTab';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import SearchableSelect from 'src/app/components/SearchableSelect/SearchableSelect';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import { formatDate } from 'src/app/helpers/time.helper';
import { useNumberMutation, useStartMutation } from 'src/app/redux/blackSystem/blackSystem.slice';
import { useAppSelector } from 'src/app/redux/hook';
import { axios } from 'src/app/utils';
import { finalUrlsConfig } from 'src/configs/url.config';
import { UploadIcon } from 'src/icons';

function BlackListComponent() {
  // States
  const [dto, setDto] = useState<string>('');
  const [numberMessage, setNumberMessage] = useState<string | number>('');
  const blackLists = ['Select Blacklist', 'Norges'];
  const [downloadState, setDownloadState] = useState<{
    state: boolean;
    done: boolean;
    size: string;
  }>({
    state: false,
    done: false,
    size: '0.00',
  });

  // AntDState
  const [form] = useForm();
  const blackListType = Form.useWatch('blacklist_type', form);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  const [start] = useStartMutation();
  const [number, { isLoading: isNumberLoading }] = useNumberMutation();

  const numberTimeoutRef: any = useRef(null);
  const exportTimeoutRef: any = useRef(null);

  useEffect(() => {
    if (isNumberLoading) {
      numberTimeoutRef.current = setTimeout(() => {
        message.error(t('blackSystemPage.stillWaitingForNumberResponse'), 5);
      }, 5000);
    } else {
      clearTimeout(numberTimeoutRef.current);
    }

    return () => {
      clearTimeout(numberTimeoutRef.current);
    };
  }, [isNumberLoading]);

  useEffect(() => {
    if (isExportLoading) {
      exportTimeoutRef.current = setTimeout(() => {
        message.error(t('blackSystemPage.stillWaitingForExportResponse'), 5);
      }, 5000);
    } else {
      clearTimeout(exportTimeoutRef.current);
    }

    return () => {
      clearTimeout(exportTimeoutRef.current);
    };
  }, [isExportLoading]);

  const { theme } = useAppSelector((state) => state.configContent);
  const debounceTimeoutRef: any = useRef(null);
  const handleNumberReq = () => {
    if (isNumberLoading) return;
    setDownloadState({
      state: false,
      done: false,
      size: '0.00',
    });
    setDto(blackListType);
    setNumberMessage('');
    if (blackListType !== blackLists[0] && blackListType !== undefined) {
      debounceTimeoutRef.current = setTimeout(() => {
        number({ data: { blacklist_type: blackListType }, url: 'Blacklist' })
          .unwrap()
          .then((res) => {
            setNumberMessage(res);
          })
          .catch(() => {
            setNumberMessage('');
            message.success(t('blackSystemPage.numberOperationFailed'), 5);
          });
      }, 500);
    }
  };

  useEffect(() => {
    handleNumberReq();
  }, [blackListType]);

  const disableConditions = ({ isExport }: { isExport?: boolean }) => {
    if (blackListType === blackLists[0]) return true;
    if (isExport) {
      if (numberMessage === 0 || !dto || !numberMessage) {
        return true;
      }
      if (isExportLoading) return true;
    }
    return false;
  };

  return (
    <div className="social-media">
      <div className="first-form">
        <div className="news-api-form">
          <FormComponent
            extraButton={
              <div className="extra-btns">
                <ExtraTab
                  onClick={() => {
                    if (numberMessage && Number(numberMessage) > 0) {
                      showModal({
                        title: t('blackSystemPage.startStartOperation'),
                        content: <p>{t('blackSystemPage.startOperationWarnText')}</p>,
                        onSave: (modal) => {
                          start({ data: { blacklist_type: dto }, url: 'Blacklist' })
                            .unwrap()
                            .catch(() => message.error(t('blackSystemPage.startOperationFailed'), 5));
                          modal.destroy();
                          message.success(t('blackSystemPage.startOperationSuccess'), 5);
                        },
                        okText: t('blackSystemPage.startOkText'),
                      });
                    } else {
                      message.success(t('blackSystemPage.startOperationSuccess'), 5);
                      start({ data: { blacklist_type: dto }, url: 'Blacklist' })
                        .unwrap()
                        .catch(() => message.error(t('blackSystemPage.startOperationFailed'), 5));
                    }
                  }}
                  icon={null}
                  text={t('blackSystemPage.commonBtns.start')}
                  bgTheme
                  disabled={disableConditions({})}
                />

                <ExtraTab
                  onClick={async () => {
                    try {
                      setIsExportLoading(true);
                      message.success(t('blackSystemPage.exportOperationSuccess'), 5);
                      await axios({
                        url: finalUrlsConfig().blackSystem('Blacklist').export,
                        method: 'POST',
                        data: { blacklist_type: dto },
                        responseType: 'blob',
                        onDownloadProgress: (progressEvent) => {
                          const bytesDownloaded = progressEvent.loaded;
                          const megabytesDownloaded = bytesDownloaded / (1024 * 1024);
                          if (megabytesDownloaded) {
                            if (megabytesDownloaded > 1) {
                              setIsExportLoading(false);
                            }
                            setDownloadState((prevState) => ({
                              ...prevState,
                              state: true,
                              size: `${megabytesDownloaded.toFixed(2)}`,
                            }));
                          }
                        },
                        blockSpinner: true,
                      })
                        .then((res: any) => {
                          setDownloadState({
                            size: '0.00',
                            state: false,
                            done: true,
                          });
                          const fileName = dto || 'Blacklist';
                          const today = new Date();
                          const formattedDate = formatDate(today);
                          fileDownload(res.data, `Blacklist-${fileName}-${formattedDate}.xlsx`);
                          setIsExportLoading(false);
                        })
                        .catch(() => {
                          message.error(t('blackSystemPage.exportOperationFailed'), 5);
                          setDownloadState({
                            done: false,
                            size: '0.00',
                            state: false,
                          });
                          setIsExportLoading(false);
                        });
                    } catch (error) {
                      console.log('error', error);
                      setIsExportLoading(false);
                    }
                  }}
                  icon={<UploadIcon />}
                  text={t('blackSystemPage.commonBtns.exportData')}
                  bgTheme
                  disabled={disableConditions({ isExport: true })}
                />
                {/* <ExtraTab
                  onClick={() => {
                    number({ data: { blacklist_type: dto }, url: 'Blacklist' })
                      .unwrap()
                      .then((res) => {
                        setNumberMessage(res);
                      })
                      .catch(() => {
                        setNumberMessage('');
                        message.success(t('blackSystemPage.numberOperationFailed'), 5);
                      });
                  }}
                  icon={null}
                  text={t('blackSystemPage.commonBtns.number')}
                  bgTheme
                  disabled={!dto}
                /> */}
              </div>
            }
            options={{ labelCol: { span: 9 }, form, size: 'middle' }}
          >
            <Form.Item
              name="blacklist_type"
              label={
                <div style={{ display: 'flex' }}>
                  {t('blackSystemPage.BlackList.selectBlackList')}
                  <Tooltip placement="topRight" title={t('blackSystemPage.BlackList.selectBlackList')}>
                    <Badge offset={[10, 0]} />
                  </Tooltip>
                </div>
              }
              rules={[
                {
                  required: true,
                },
              ]}
              initialValue={blackLists[0]}
            >
              <SearchableSelect bordered={false}>
                {blackLists.map((blackList) => (
                  <Select.Option value={blackList} key={blackList}>
                    {blackList}
                  </Select.Option>
                ))}
              </SearchableSelect>
            </Form.Item>
          </FormComponent>
          <div className="resultAreas">
            {(downloadState.done || downloadState.state) && (
              <div
                className="resultArea"
                style={{
                  ...(theme.colors.quoteBgc && {
                    borderTop: `1px solid ${theme.colors.quoteBgc}`,
                    borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                    color: `${theme.colors.quoteBgc}`,
                  }),
                }}
              >
                {downloadState.state ? (
                  <>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    <p>{downloadState.size}mb</p>
                  </>
                ) : (
                  downloadState.done && (
                    <>
                      <Progress type="circle" percent={100} size={30} />
                      <p>{t('blackSystemPage.successfullyDownloaded')}</p>
                    </>
                  )
                )}
              </div>
            )}
            {numberMessage !== '' && !isNumberLoading && (
              <div
                className="resultArea"
                style={{
                  ...(theme.colors.quoteBgc && {
                    borderTop: `1px solid ${theme.colors.quoteBgc}`,
                    borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                    color: `${theme.colors.quoteBgc}`,
                  }),
                }}
              >
                <Button className="refetchBtn" onClick={handleNumberReq}>
                  {t('blackSystemPage.refetchBtn')} <ReloadOutlined style={{ fontSize: '1.25' }} />
                </Button>
                <p>
                  {t('blackSystemPage.BlackList.numberMessage')}
                  {numberMessage}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BlackListComponent;
